import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Search, Users, KeyRound, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

export default function Employees({ user }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee'
  });

  const location = useLocation();

  useEffect(() => {
    fetchEmployees();
    const interval = setInterval(fetchEmployees, 30000);
    const onFocus = () => fetchEmployees();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/users`, {
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/users/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add employee');
      }

      toast.success('Employee added successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'employee' });
      fetchEmployees();
    } catch (error) {
      toast.error(error.message || 'Failed to add employee');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) return;

    try {
      const response = await authFetch(`${API}/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to remove employee');

      toast.success('Employee removed');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedEmployee || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await authFetch(`${API}/users/${selectedEmployee.user_id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reset password');
      }

      toast.success(`Password reset for ${selectedEmployee.name || selectedEmployee.email}`);
      setResetDialogOpen(false);
      setSelectedEmployee(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
    }
  };

  const openResetDialog = (emp) => {
    setSelectedEmployee(emp);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const handleToggleNotifications = async (emp) => {
    try {
      const response = await authFetch(`${API}/users/${emp.user_id}/notifications`, {
        method: 'PUT',
      });
      if (!response.ok) throw new Error('Failed to update');
      const data = await response.json();
      toast.success(`Notifications ${data.notify_schedule ? 'enabled' : 'disabled'} for ${emp.name || emp.email}`);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update notification setting');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const query = searchQuery.toLowerCase();
    return (
      (emp.name && emp.name.toLowerCase().includes(query)) ||
      (emp.email && emp.email.toLowerCase().includes(query)) ||
      (emp.role && emp.role.toLowerCase().includes(query))
    );
  });

  return (
    <Layout user={user}>
      <div className="p-6 lg:p-12 space-y-8 bg-slate-200 min-h-screen">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="employees-title">Employees</h1>
            <p className="text-slate-600 mt-2">Manage employee accounts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button">
                <Plus className="h-4 w-4 mr-2" />
                ADD EMPLOYEE
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">Add Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Full Name</Label>
                  <Input
                    data-testid="employee-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Email</Label>
                  <Input
                    data-testid="employee-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="employee@company.com"
                    required
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Password</Label>
                  <Input
                    data-testid="employee-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="employee-role-select" className="bg-slate-50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="administrator">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full uppercase font-bold" data-testid="employee-submit-button">
                  Add Employee
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50 border-slate-200"
              data-testid="employee-search-input"
            />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="employees-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Notify</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      {searchQuery ? `No employees found matching "${searchQuery}"` : 'No employees found'}
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp, index) => (
                    <tr key={emp.user_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`employee-row-${index}`}>
                      <td className="px-4 py-4 font-semibold text-sm">{emp.name || '-'}</td>
                      <td className="px-4 py-4 text-sm">{emp.email || '-'}</td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                          emp.role === 'administrator' || emp.role === 'admin'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {emp.role === 'administrator' || emp.role === 'admin' ? 'Administrator' : 'Employee'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleNotifications(emp)}
                          data-testid={`toggle-notify-${index}`}
                          title={emp.notify_schedule === false ? 'Enable notifications' : 'Disable notifications'}
                        >
                          {emp.notify_schedule === false
                            ? <BellOff className="h-4 w-4 text-slate-400" />
                            : <Bell className="h-4 w-4 text-green-500" />
                          }
                        </Button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetDialog(emp)}
                            data-testid={`reset-password-${index}`}
                            title="Reset Password"
                          >
                            <KeyRound className="h-4 w-4 text-blue-500" />
                          </Button>
                          {emp.user_id !== user?.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(emp.user_id)}
                              data-testid={`delete-employee-${index}`}
                              title="Delete Employee"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Reset Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Reset password for: <strong>{selectedEmployee?.name || selectedEmployee?.email}</strong>
              </p>
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-slate-50 border-slate-200"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setResetDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 uppercase font-bold"
                  onClick={handleResetPassword}
                  data-testid="reset-password-submit"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
